<script lang="ts" setup>
import { reactive, ref } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import { MAX_ARTICLE_MEMO_LENGTH } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import InputTag from '@/components/common/atoms/input-tag.vue';
import TextCounter from '@/components/common/molecules/text-counter.vue';
import { AdpDocument, ThemeList } from '@/types';
import { useUserInfo, useUserTags } from '@/utils/swr';

interface Props {
  adpDocument: AdpDocument;
  // モーダルの記事表示の閲覧のため
  // TODO: 閲覧処理は親のコンポーネントに移す; AdpDocumentCard をプレビューカードなどにリファクタする
  theme?: ThemeList;
  pageName: PageName;
  feature?: Feature;
}

const props = defineProps<Props>();

type Submit = { tags?: string[]; memo?: string };
const emit = defineEmits<{
  close: [];
  submit: [Submit];
}>();

const { data: userInfo } = useUserInfo();
const article = props.adpDocument;
const theme = props.theme;
const pageName = props.pageName;
const feature = props.feature;

const { data: tags } = useUserTags();

const selectedTags = reactive({ list: [] as string[] });
const editingMemo = ref('');
const isMarkedGroup = ref(false);

const hideDialog = () => {
  emit('close');
  return true;
};

const submit = async () => {
  emit('submit', { tags: selectedTags.list, memo: editingMemo.value });
};
</script>

<template>
  <DgrDialog
    v-if="userInfo && userInfo.id && tags && tags.user_tags"
    :open="true"
    title="マーク"
    :close-interceptor-handler="hideDialog"
    @cancel="hideDialog"
    :close-on-escape="false"
  >
    <template #description>
      <div class="o-body c-text-regular--m">
        <AdpDocumentCard
          :adp-document="article"
          :theme-id="theme ? theme.id : undefined"
          :show-comment="false"
          :show-user-action="false"
          :page-name="pageName"
          :feature="feature"
        ></AdpDocumentCard>
        <div class="spacing-08"></div>
        <InputTag
          :tag-list="tags.user_tags"
          v-model="selectedTags.list"
          placeholder="マイタグを入力後、改行でマイタグを追加"
          :is-editing="true"
          :is-group="false"
        ></InputTag>
        <div class="c-formBlock">
          <div class="c-formBlock__label">マイメモ</div>
          <textarea
            class="o-article-memo c-textArea"
            v-model.trim="editingMemo"
            :maxlength="MAX_ARTICLE_MEMO_LENGTH"
            placeholder="マイメモを入力"
            @keydown.ctrl.enter="submit"
            @keydown.meta.enter="submit"
          ></textarea>
          <TextCounter
            :text="editingMemo"
            :max-length="MAX_ARTICLE_MEMO_LENGTH"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="o-footer c-text-regular--m">
        <button
          class="o-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
          :class="{ disabled: isMarkedGroup }"
          :disabled="isMarkedGroup"
          @click="submit"
        >
          マーク
        </button>
      </div>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.o-article {
  display: flex;
  align-items: center;
  border-radius: 4px;
  border: 1px solid $color-gray400;
  .o-article-info {
    width: 484px;
    padding: 16px;
    box-sizing: border-box;
    .o-article-info__title {
      font-family: Noto Sans CJK JP;
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
    }
    .o-article-info__media {
      color: $color-gray600;
    }
  }
  .o-article-image {
    width: 100px;
    height: 100px;
    object-fit: cover;
    object-position: 50% 25%;
    border-left: 1px solid $color-gray400;
    border-radius: 0 3px 3px 0;
  }
}
.o-article-memo {
  height: 140px;
  resize: none;
  outline: none;
}

textarea::placeholder {
  color: $color-gray600;
}

.o-body {
  width: 616px;
}
.o-footer {
  margin-top: -16px;
}
</style>
